<div class="app-dialog">
  <div class="app-dialog__heading">
    <button mat-icon-button type="button" (click)="onClose()" class="start-project-dialog-close" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
    <div class="app-dialog__heading-copy">
      <span class="app-dialog__eyebrow">Project</span>
      <div class="app-dialog__title-row">
        <div class="app-dialog__title-copy">
          <h2 mat-dialog-title class="app-dialog__title">How to Start: {{ data.project.projectName }}</h2>
          <p>Use the API below to start processing the project's uploaded file.</p>
        </div>
      </div>
    </div>
  </div>

  <mat-dialog-content class="app-dialog__content">
    <div class="dialog-content">
      <div class="api-listener-section">
        <div class="info-banner info-box">
          <mat-icon svgIcon="info-circle"></mat-icon>
          <span>This project uses an uploaded <strong>{{ getDataSourceTypeName() }}</strong> file. Use the API below to trigger processing of the file already uploaded to the project.</span>
        </div>

        <mat-button-toggle-group [(ngModel)]="selectedApiCall" class="api-call-toggle">
          <mat-button-toggle value="start">1. Start Cleaning</mat-button-toggle>
          <mat-button-toggle value="status">2. Get Status</mat-button-toggle>
          <mat-button-toggle value="results">3. Get Results</mat-button-toggle>
        </mat-button-toggle-group>

        <p *ngIf="selectedApiCall === 'start'" class="api-instructions">To run the workflow using the uploaded file, call the endpoint below. The request is a POST but you do not need to include a request body; the project will use the previously uploaded file.</p>
        <p *ngIf="selectedApiCall === 'status'" class="api-instructions">After making the start call, you will receive an action token. Use this token to poll the endpoint below every few seconds until the status is <strong>2 (Complete)</strong> or <strong>3 (Failed)</strong>.</p>
        <p *ngIf="selectedApiCall === 'results'" class="api-instructions">Once the status endpoint returns a status of <strong>2 (Complete)</strong>, call the endpoint below using the same action token to retrieve the cleaned data.</p>

        <div *ngIf="selectedApiCall === 'start'" class="api-config-section">
          <div class="config-item">
            <label>Endpoint</label>
            <div class="input-with-copy">
              <input type="text" [value]="endpoint" readonly class="endpoint-input">
              <button mat-icon-button (click)="copyToClipboard(endpoint, 'endpoint')" [title]="endpointJustCopied ? 'Copied!' : 'Copy endpoint'">
                <mat-icon [class.copied]="endpointJustCopied">{{ endpointJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
            </div>
          </div>

          <div class="config-item">
            <label>Token</label>
            <div class="input-with-copy">
              <input type="text" [value]="token" readonly class="token-input" [class.placeholder-token]="!isTokenAvailable()">
              <button 
                *ngIf="isTokenAvailable()"
                mat-icon-button 
                (click)="copyToClipboard(token, 'token')" 
                [title]="tokenJustCopied ? 'Copied!' : 'Copy token'">
                <mat-icon [class.copied]="tokenJustCopied">{{ tokenJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
              <button 
                *ngIf="!isTokenAvailable()"
                mat-stroked-button 
                class="regenerate-btn"
                (click)="regenerateToken()" 
                [disabled]="isRegeneratingToken">
                <mat-icon *ngIf="!isRegeneratingToken">refresh</mat-icon>
                <mat-spinner *ngIf="isRegeneratingToken" diameter="20"></mat-spinner>
                {{ isRegeneratingToken ? 'Regenerating...' : 'Regenerate Token' }}
              </button>
            </div>
            <div *ngIf="tokenRegenerationError" class="error-message">
              <mat-icon svgIcon="error"></mat-icon>
              {{ tokenRegenerationError }}
            </div>
          </div>

          <div class="config-item">
            <label>Request Method</label>
            <div class="method-badge">POST</div>
          </div>

          <div class="config-item">
            <label>Code Examples</label>
            <mat-tab-group class="code-samples-tabs">
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="curl"></mat-icon>
                    <span>cURL</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getCurlCommand()" [language]="'bash'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getCurlCommand(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="csharp"></mat-icon>
                    <span>C#</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getCSharpCode()" [language]="'csharp'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getCSharpCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="vbnet"></mat-icon>
                    <span>VB.NET</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getVBNetCode()" [language]="'vbnet'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getVBNetCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="python"></mat-icon>
                    <span>Python</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getPythonCode()" [language]="'python'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getPythonCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon>code</mat-icon>
                    <span>Java</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getJavaCode()" [language]="'java'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getJavaCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>

        <div *ngIf="selectedApiCall === 'status'" class="api-config-section">
          <div class="config-item">
            <label>Endpoint</label>
            <div class="input-with-copy">
              <input type="text" [value]="statusEndpoint" readonly class="endpoint-input">
              <button mat-icon-button (click)="copyToClipboard(statusEndpoint, 'endpoint')" [title]="endpointJustCopied ? 'Copied!' : 'Copy endpoint'">
                <mat-icon [class.copied]="endpointJustCopied">{{ endpointJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
            </div>
            <p class="field-hint">Replace <code>{{ '{' }}ACTION_TOKEN{{ '}' }}</code> with the token returned from the start call.</p>
          </div>

          <div class="config-item">
            <label>Authorization Token</label>
            <div class="input-with-copy">
              <input type="text" [value]="token" readonly class="token-input" [class.placeholder-token]="!isTokenAvailable()">
              <button 
                *ngIf="isTokenAvailable()"
                mat-icon-button 
                (click)="copyToClipboard(token, 'token')" 
                [title]="tokenJustCopied ? 'Copied!' : 'Copy token'">
                <mat-icon [class.copied]="tokenJustCopied">{{ tokenJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
              <button 
                *ngIf="!isTokenAvailable()"
                mat-stroked-button 
                class="regenerate-btn"
                (click)="regenerateToken()" 
                [disabled]="isRegeneratingToken">
                <mat-icon *ngIf="!isRegeneratingToken">refresh</mat-icon>
                <mat-spinner *ngIf="isRegeneratingToken" diameter="20"></mat-spinner>
                {{ isRegeneratingToken ? 'Regenerating...' : 'Regenerate Token' }}
              </button>
            </div>
            <div *ngIf="tokenRegenerationError" class="error-message">
              <mat-icon svgIcon="error"></mat-icon>
              {{ tokenRegenerationError }}
            </div>
          </div>

          <div class="config-item">
            <label>Request Method</label>
            <div class="method-badge">GET</div>
          </div>

          <div class="config-item">
            <label>Code Examples</label>
            <mat-tab-group class="code-samples-tabs">
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="curl"></mat-icon>
                    <span>cURL</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getStatusCurlCommand()" [language]="'bash'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusCurlCommand(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="csharp"></mat-icon>
                    <span>C#</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getStatusCSharpCode()" [language]="'csharp'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusCSharpCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="vbnet"></mat-icon>
                    <span>VB.NET</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getStatusVBNetCode()" [language]="'vbnet'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusVBNetCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="python"></mat-icon>
                    <span>Python</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getStatusPythonCode()" [language]="'python'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusPythonCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon>code</mat-icon>
                    <span>Java</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getStatusJavaCode()" [language]="'java'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusJavaCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>

        <div *ngIf="selectedApiCall === 'results'" class="api-config-section">
          <div class="config-item">
            <label>Endpoint</label>
            <div class="input-with-copy">
              <input type="text" [value]="resultsEndpoint" readonly class="endpoint-input">
              <button mat-icon-button (click)="copyToClipboard(resultsEndpoint, 'endpoint')" [title]="endpointJustCopied ? 'Copied!' : 'Copy endpoint'">
                <mat-icon [class.copied]="endpointJustCopied">{{ endpointJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
            </div>
            <p class="field-hint">Replace <code>{{ '{' }}ACTION_TOKEN{{ '}' }}</code> with the token returned from the start call.</p>
          </div>

          <div class="config-item">
            <label>Authorization Token</label>
            <div class="input-with-copy">
              <input type="text" [value]="token" readonly class="token-input" [class.placeholder-token]="!isTokenAvailable()">
              <button 
                *ngIf="isTokenAvailable()"
                mat-icon-button 
                (click)="copyToClipboard(token, 'token')" 
                [title]="tokenJustCopied ? 'Copied!' : 'Copy token'">
                <mat-icon [class.copied]="tokenJustCopied">{{ tokenJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
              <button 
                *ngIf="!isTokenAvailable()"
                mat-stroked-button 
                class="regenerate-btn"
                (click)="regenerateToken()" 
                [disabled]="isRegeneratingToken">
                <mat-icon *ngIf="!isRegeneratingToken">refresh</mat-icon>
                <mat-spinner *ngIf="isRegeneratingToken" diameter="20"></mat-spinner>
                {{ isRegeneratingToken ? 'Regenerating...' : 'Regenerate Token' }}
              </button>
            </div>
            <div *ngIf="tokenRegenerationError" class="error-message">
              <mat-icon svgIcon="error"></mat-icon>
              {{ tokenRegenerationError }}
            </div>
          </div>

          <div class="config-item">
            <label>Request Method</label>
            <div class="method-badge">GET</div>
          </div>

          <div class="config-item">
            <label>Code Examples</label>
            <mat-tab-group class="code-samples-tabs">
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="curl"></mat-icon>
                    <span>cURL</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getResultsCurlCommand()" [language]="'bash'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsCurlCommand(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="csharp"></mat-icon>
                    <span>C#</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getResultsCSharpCode()" [language]="'csharp'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsCSharpCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="vbnet"></mat-icon>
                    <span>VB.NET</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getResultsVBNetCode()" [language]="'vbnet'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsVBNetCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="python"></mat-icon>
                    <span>Python</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getResultsPythonCode()" [language]="'python'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsPythonCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon>code</mat-icon>
                    <span>Java</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <pre><code [highlight]="getResultsJavaCode()" [language]="'java'" (click)="selectCodeText($event)"></code></pre>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsJavaCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>
      </div>
    </div>
  </mat-dialog-content>
</div>
